import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";

// Layout Components
import Header from "./components/Header";
import Footer from "./components/Footer";

// Pages
import HomePage from "./pages/HomePage";
import AboutPage from "./pages/AboutPage";
import { ServicesPage, ServiceDetailPage } from "./pages/ServicesPage";
import GalleryPage from "./pages/GalleryPage";
import FAQPage from "./pages/FAQPage";
import ContactPage from "./pages/ContactPage";
import BookEstimatePage from "./pages/BookEstimatePage";
import AdminLoginPage from "./pages/AdminLoginPage";
import AdminDashboard from "./pages/AdminDashboard";
import LandingPage from "./pages/LandingPage";
import CustomerPortal from "./pages/CustomerPortal";
import AreasPage from "./pages/AreasPage";
import LocationPage from "./pages/LocationPage";
import BlogPage from "./pages/BlogPage";
import BlogPostPage from "./pages/BlogPostPage";
import AuthCallback from "./pages/AuthCallback";
import SetPasswordPage from "./pages/SetPasswordPage";
import CompleteProfileModal from "./pages/CompleteProfileModal";
import ServiceLocationPage from "./pages/ServiceLocationPage";

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, user, setGoogleUser } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-primary-dark flex items-center justify-center">
        <div className="animate-pulse text-white">Loading...</div>
      </div>
    );
  }

  // If user was passed from AuthCallback, they're authenticated
  const authUser = location.state?.user || user;
  
  if (!isAuthenticated && !location.state?.user) {
    return <Navigate to="/admin" replace />;
  }

  // Show profile completion modal if needed
  if (authUser && authUser.profile_complete === false) {
    return (
      <div className="min-h-screen bg-primary-dark">
        <CompleteProfileModal
          user={authUser}
          onComplete={(updatedUser) => setGoogleUser(updatedUser)}
        />
      </div>
    );
  }
  
  return children;
};

// Public Layout with Header and Footer
const PublicLayout = ({ children }) => {
  return (
    <>
      <Header />
      <main>{children}</main>
      <Footer />
    </>
  );
};

function AppRoutes() {
  const location = useLocation();

  // CRITICAL: Check URL fragment for session_id synchronously during render
  // This prevents race conditions by processing OAuth callback FIRST
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<PublicLayout><HomePage /></PublicLayout>} />
      <Route path="/about" element={<PublicLayout><AboutPage /></PublicLayout>} />
      <Route path="/services" element={<PublicLayout><ServicesPage /></PublicLayout>} />
      <Route path="/services/:slug" element={<PublicLayout><ServiceDetailPage /></PublicLayout>} />
      <Route path="/gallery" element={<PublicLayout><GalleryPage /></PublicLayout>} />
      <Route path="/faq" element={<PublicLayout><FAQPage /></PublicLayout>} />
      <Route path="/contact" element={<PublicLayout><ContactPage /></PublicLayout>} />
      <Route path="/book-estimate" element={<PublicLayout><BookEstimatePage /></PublicLayout>} />
      
      {/* Blog Pages */}
      <Route path="/blog" element={<PublicLayout><BlogPage /></PublicLayout>} />
      <Route path="/blog/:slug" element={<PublicLayout><BlogPostPage /></PublicLayout>} />

      {/* Area/Location Pages */}
      <Route path="/areas" element={<PublicLayout><AreasPage /></PublicLayout>} />
      <Route path="/areas/:slug" element={<PublicLayout><LocationPage /></PublicLayout>} />

      {/* Landing Pages (No header/footer for PPC) */}
      <Route path="/lp/:slug" element={<LandingPage />} />
      
      {/* Customer Portal (No header/footer) */}
      <Route path="/portal/:jobId" element={<CustomerPortal />} />
      
      {/* Admin Routes */}
      <Route path="/admin" element={<AdminLoginPage />} />
      <Route path="/admin/set-password" element={<SetPasswordPage />} />
      <Route 
        path="/admin/dashboard" 
        element={
          <ProtectedRoute>
            <AdminDashboard />
          </ProtectedRoute>
        } 
      />
      
      {/* Service + Location SEO Pages (catch-all before 404) */}
      <Route path="/:slug" element={<PublicLayout><ServiceLocationPage /></PublicLayout>} />
      
      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
